import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, Loader2, CheckSquareIcon, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isBuyer, isSeller, isAuthed } from "../../services/auth.service";
import { getUserCompany, createCompanyLaunch, checkHasActiveLaunch, checkIfCompanyExists, createSellerJoinNow, createCompanyForLaunch, getAvailableLaunches, getAllIndustries } from "../../utils/api";
import ScrollToTop from "../../components/layout/ScrollToTop";

const CompanyInfoDisplay = ({ company }) => (
  <div className="space-y-4 bg-purple-50 p-6 rounded-lg border border-purple-100">
    <h3 className="text-lg font-medium text-purple-900">Company Information</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-2">
        <p className="text-sm text-purple-600">Company Name</p>
        <p className="font-medium text-gray-900">{company.name}</p>
      </div>
      <div className="space-y-2">
        <p className="text-sm text-purple-600">Website</p>
        <p className="font-medium text-gray-900">{company.website}</p>
      </div>
      <div className="space-y-2">
        <p className="text-sm text-purple-600">Category</p>
        <p className="font-medium text-gray-900">{company.category}</p>
      </div>
    </div>
    <div className="space-y-2 pt-2">
      <p className="text-sm text-purple-600">Description</p>
      <p className="font-medium text-gray-900">{company.description}</p>
    </div>
  </div>
);

const ActiveLaunchBanner = () => (
  <div className="p-4 rounded-lg bg-purple-50 border border-purple-100 flex items-start space-x-3">
    <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5" />
    <div>
      <h4 className="text-sm font-medium text-purple-800">Active Launch in Progress</h4>
      <p className="mt-1 text-sm text-purple-600">
        You currently have an active product launch. You can submit another product once your current launch is complete.
      </p>
    </div>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="p-4 rounded-lg bg-red-50 border border-red-100 flex items-start space-x-3">
    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
    <div>
      <h4 className="text-sm font-medium text-red-800">Submission Failed</h4>
      <p className="mt-1 text-sm text-red-600">
        {message || "There was an error submitting your application. Please try again."}
      </p>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen flex flex-col items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
    <p className="mt-4 text-sm text-gray-600">Loading...</p>
  </div>
);

const SubmitProduct = () => {
  const navigate = useNavigate();
  const [userCompany, setUserCompany] = useState(null);
  const [hasActiveLaunch, setHasActiveLaunch] = useState(false);
  const [launchType, setLaunchType] = useState("specific");
  const [loading, setLoading] = useState(true);
  const [loadingCheck, setLoadingCheck] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [category, setCategory] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [features, setFeatures] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [availableLaunches, setAvailableLaunches] = useState([]);
  const [loadedLaunches, setLoadedLaunches] = useState(false);
  const [selectedLaunchId, setSelectedLaunchId] = useState(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState(0);
  const [industries, setIndustries] = useState([]);
  const isLoggedIn = isAuthed();

  function getCookie(cname) {
    let name = cname + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  useEffect(() => {
    async function fetchIndustries() {
      try {
        const response = await getAllIndustries();
        if (response.success) {
          setIndustries(response.data);
        } else {
          console.error("Failed to fetch industries:", response.message);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
      }
    }

    async function fetchAvailableLaunches() {
      try {
        const response = await getAvailableLaunches();
        if (response.success) {
          setAvailableLaunches(response.data);
          setLoadedLaunches(true);
        } else {
          console.error("Failed to fetch available launches:", response.message);
        }
      } catch (error) {
        console.error("Error fetching available launches:", error);
      }
    }

    async function fetchUserCompany() {
      try {
        const response = await getUserCompany();
        if (response.success) {
          setUserCompany(response.data);
        } else {
          console.error("Failed to fetch user company:", response.message);
        }
      } catch (error) {
        console.error("Error fetching user company:", error);
      } finally {
        setLoading(false);
      }
    }

    async function checkActiveLaunch() {
      try {
        const response = await checkHasActiveLaunch();
        if (response.success) {
          setHasActiveLaunch(response.data);
          setLoadingCheck(false);
        } else {
          console.error("Failed to check active launch:", response.message);
          setLoadingCheck(false);
        }
      } catch (error) {
        console.error("Error checking active launch:", error);
      }
    }

    fetchIndustries();
    fetchAvailableLaunches();
    checkActiveLaunch();
    fetchUserCompany();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedLaunchId == null){
      alert("Please select a launch date");
      return;
    }
    
    if (isAuthed()) {
      setIsSubmitting(true);

      try {
        let res = await createCompanyLaunch(launchType, selectedLaunchId);
        if (res.success) {
          navigate('/launch/confirmation');
        }
      } catch (error) {
        console.error('Failed to submit launch:', error);
        setHasError(true);
        setErrorMessage('Failed to submit launch. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }

    else{
      handleSubmitNew();
      setIsSubmitting(true);
    }
  }

  function setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = 'expires=' + d.toUTCString();
    let _domain = 'domain=leadrpro.com';
    document.cookie = cname + '=' + cvalue + ';' + _domain + ';' + expires + ';path=/';
  }

  async function handleSubmitNew() {
    setHasError(false);
    setErrorMessage('');

    if (selectedLaunchId == null){
      alert("Please select a launch date");
      return;
    }

    let exists = await checkCompanyExists();

    if (exists.success && exists.data == true) {
      alert("Company already exists. Please log in to continue.");
      setSubmitting(false);
    }

    else{
      let channel_token = getCookie('channel_token');
      let partner_key = getCookie('ps_partner_key');
      let click_id = getCookie('ps_xid');
      let l_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
      if (email.length === 0){
          setHasError(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setErrorMessage('Email is required');
          return;
      }
  
      let data= {email, password, optIn: true, promoCode: '', source: '', channel_token, timezone: l_timezone, referrer: '', partner_key, click_id};
      let res  = await createSellerJoinNow(data);
  
      if (res.success){
        localStorage.setItem('access_token', res.data.accessToken);
        localStorage.setItem('refresh_token', res.data.refreshToken);
  
        // used for meetings.leadrpro
        setCookie('access_token', res.data.accessToken, 1);

        let res2 = await createCompanyForLaunch(companyName, website, shortDescription, longDescription, features, targetAudience, launchType, selectedIndustryId);

        if (res2.success){ 
          let company_id = res2.data;
          let res3 = await createCompanyLaunch(launchType, selectedLaunchId);

          if (res3.success) {
          window.location = '/launch/confirmation';
          }
        }
      }
      else{
        setSubmitting(false);
        setIsSubmitting(false);
          setLoading(false);
          setHasError(true);
          setErrorMessage(res.message);
      }
  
      setSubmitting(false);
    }
  }

  async function checkCompanyExists() {
    try {
      const response = await checkIfCompanyExists(companyName, website);
      if (response.success) {
        console.log("Company exists:", response.data);
        return true;
      } else {
        console.error("Company does not exist:", response.message);
        return false;
      }
    } catch (error) {
      console.error("Error checking company existence:", error);
      return false;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollToTop />
      {loading ? (
        <LoadingState />
      ) : (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/launch" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 mb-6">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Home
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Submit Your Product for Launch</h1>
            <p className="text-lg text-gray-600">
              Get your product in front of 50,000+ decision makers and compete for our weekly badges.
            </p>
          </div>

          <div className="bg-white rounded-lg border shadow-sm">
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b">
                <h2 className="text-xl font-semibold">Product Launch Application</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Fill in the details below to apply for our weekly product launch competition.
                  Limited to 10 spots per week.
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Company Information Section */}
                {isLoggedIn && userCompany ? (
                  <CompanyInfoDisplay company={userCompany} />
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Company Information</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                          Company Name *
                        </label>
                        <input
                          id="companyName"
                          name="companyName"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="e.g., LeadrPro"
                          required
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                          Website *
                        </label>
                        <input
                          id="website"
                          name="website"
                          value={website}
                          required
                          onChange={(e) => setWebsite(e.target.value)}
                          placeholder="e.g., https://leadrpro.com"
                          
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                        Category *
                      </label>

                      <select className='input select' value={selectedIndustryId} onChange={(e) => setSelectedIndustryId(e.target.value)}>
                  <option value="0">Select industry...</option>
                  {industries.map(item => (
                    <>
                      <option value={item.industry_id}>{item.name}</option>
                    </>
                  ))}
                </select>

                      {/* <input
                        id="category"
                        name="category"
                        value={category}
                        rquired
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="e.g., Marketing, HR, Development, Sales"
                      
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      /> */}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">
                        Short Description (50 characters max)
                      </label>
                      <input
                        id="shortDescription"
                        name="shortDescription"
                        value={shortDescription}
                        onChange={(e) => setShortDescription(e.target.value)}
                        placeholder="A brief one-line description of your product"
                        maxLength={50}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="longDescription" className="block text-sm font-medium text-gray-700">
                        Detailed Description
                      </label>
                      <textarea
                        id="longDescription"
                        name="longDescription"
                        value={longDescription}
                        onChange={(e) => setLongDescription(e.target.value)}
                        placeholder="Provide a comprehensive description of your product and what it does"
                        rows={4}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}

                {/* Contact Information Section - Only show if not logged in */}
                {!isLoggedIn && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email Address *
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="contact@company.com"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password *
                        </label>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      {/* <div className="space-y-2">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          value={phone}
                          onChange={handleChange}
                          placeholder="Optional"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div> */}
                    </div>
                  </div>
                )}

                {/* Product Details Section */}
                {!isLoggedIn && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Product Details</h3>

                    <div className="space-y-2">
                      <label htmlFor="features" className="block text-sm font-medium text-gray-700">
                        Key Features *
                      </label>
                      <textarea
                        id="features"
                        name="features"
                        value={features}
                        onChange={(e) => setFeatures(e.target.value)}
                        placeholder="List your product's key features (one per line)"
                        rows={4}

                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700">
                        Target Audience *
                      </label>
                      <textarea
                        id="targetAudience"
                        name="targetAudience"
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        placeholder="Describe your ideal customer profile (company size, industry, role)"
                        rows={3}
                        
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}
                {/* Product Details End */}

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Launch Preference</h3>

                  <div className="space-y-4">
                    {/* <div className="flex items-start space-x-2">
                      <input
                        type="radio"
                        id="next"
                        name="launchWeek"
                        value="next"
                        checked={launchType === "next"}
                        onChange={(e) => setLaunchType('next')}
                        className="mt-1 h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div>
                        <label htmlFor="next" className="block text-sm font-medium text-gray-700">
                          Next available week (recommended)
                        </label>
                        <p className="text-sm text-gray-500">We'll place you in the next week with available spots</p>
                      </div>
                    </div> */}
                    <div className="flex items-start space-x-2">
                      <input
                        type="radio"
                        id="specific"
                        name="launchWeek"
                        value="specific"
                        checked={launchType === "specific"}
                        onChange={(e) => setLaunchType('specific')}
                        className="mt-1 h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div>
                        <label htmlFor="specific" className="block text-sm font-medium text-gray-700">
                          I'd like to request a specific week
                        </label>
                        <p className="text-sm text-gray-500">Subject to availability, our team will contact you</p>
                      </div>
                    </div>
                    
                    {launchType === "specific" && loadedLaunches && (
                      <div className="mt-4 pl-6">
                        <label htmlFor="availableLaunch" className="block text-sm font-medium text-gray-700">
                          Select Preferred Launch Week
                        </label>
                        <select
                          id="availableLaunch"
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                          value={selectedLaunchId}
                          required
                          onChange={(e) => setSelectedLaunchId(e.target.value)}
                        >
                          <option value="">Select a launch week...</option>
                          {availableLaunches.map((launch) => (
                            <option key={launch.launch_id} value={launch.launch_id}>
                              {launch.name} - {new Date(launch.launch_date_start).toLocaleDateString()} - {new Date(launch.launch_date_end).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-2 pt-4">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <label htmlFor="terms" className="block text-sm font-medium text-gray-700">
                      Accept terms and conditions *
                    </label>
                    <p className="text-sm text-gray-500">
                      By checking this box, you agree to our{" "}
                      <Link to="/terms" className="text-purple-600 hover:text-purple-500">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" className="text-purple-600 hover:text-purple-500">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t">
                {hasActiveLaunch && loadingCheck == false ? (
                  <ActiveLaunchBanner />
                ) : (
                  <div className="space-y-4">
                    {hasError && <ErrorBanner message={errorMessage} />}
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
                      >
                        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="mt-8 p-6 bg-gray-100 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium mb-4">What happens after you submit?</h3>

            <ol className="space-y-4">
              <li className="flex">
                <div className="bg-purple-100 text-purple-800 w-6 h-6 rounded-full flex items-center justify-center mr-3 font-medium flex-shrink-0">1</div>
                <div>
                  <p className="font-medium">Application Review</p>
                  <p className="text-sm text-gray-600">Our team will review your application within 2 business days</p>
                </div>
              </li>
              <li className="flex">
                <div className="bg-purple-100 text-purple-800 w-6 h-6 rounded-full flex items-center justify-center mr-3 font-medium flex-shrink-0">2</div>
                <div>
                  <p className="font-medium">Confirmation & Scheduling</p>
                  <p className="text-sm text-gray-600">If approved, you'll receive a confirmation email with your launch date</p>
                </div>
              </li>
              <li className="flex">
                <div className="bg-purple-100 text-purple-800 w-6 h-6 rounded-full flex items-center justify-center mr-3 font-medium flex-shrink-0">3</div>
                <div>
                  <p className="font-medium">Profile Setup</p>
                  <p className="text-sm text-gray-600">Complete your company profile and prepare marketing materials</p>
                </div>
              </li>
              <li className="flex">
                <div className="bg-purple-100 text-purple-800 w-6 h-6 rounded-full flex items-center justify-center mr-3 font-medium flex-shrink-0">4</div>
                <div>
                  <p className="font-medium">Launch Day</p>
                  <p className="text-sm text-gray-600">Your product goes live on the platform and the competition begins!</p>
                </div>
              </li>
            </ol>
          </div>
        </main>
      )}
    </div>
  );
};

export default SubmitProduct;